import { cloneDeep, isEmpty, isEqual } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  createTargetingPreset,
  defaultTargetingPreset,
  getTargetingPreset,
  IPresetConflict,
  ITargetingPreset,
  patchTargetingPreset,
  validatePatchTargetingPreset,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { FormBuilder } from '~/src/dash/components/forms/formBuilder';
import { validateName } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import { IItemIDNameRoute } from '~/src/dash/components/tables/IDNameTable/common';
import { TextEditor } from '~/src/dash/components/text/TextEditor';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  parseLocation,
  RouteTargetingList,
  RouteTargetingPresetExisting,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TargetingPresetFormContext } from '~/src/dash/views/tools/TargetingPresets/contexts';
import {
  extractSuperUserOnlyTargetingPresetParamKeys,
  generateTargetingPresetParameterOptions,
  ITargetingPresetCategory,
  ITargetingPresetFormConfig,
  ITargetingPresetParam,
  TargetingPresetCategory,
  TargetingPresetParamKey,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';
import { TargetingConflictsDrawer } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingConflictsDrawer';
import { TargetingPresetAssociatedBadge } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingPresetAssociatedBadge';
import { UpdateTargetingPresetDialog } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/UpdateTargetingPresetDialog';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

const iconColWidth = '40px';

const getTargetingPresetEditRoute = (
  presetID: number,
  session: ISessionState,
  user: IUserState,
): string => {
  const test = getRoutePath(RouteTargetingPresetExisting, extractRouteParams({ session, user }), {
    targetingpresetid: presetID,
  });
  return test;
};

export const extractActiveSuperUserParamKeys = (
  paramKeys: string[],
  paramCfg: Record<TargetingPresetParamKey, ITargetingPresetParam>,
) => {
  const activeSuperUserParamKeys: TargetingPresetParamKey[] = [];
  if (paramKeys) {
    paramKeys.forEach((paramKey) => {
      if (paramCfg[paramKey as TargetingPresetParamKey].isSuperUserOnly) {
        activeSuperUserParamKeys.push(paramKey as TargetingPresetParamKey);
      }
    });
  }
  return activeSuperUserParamKeys;
};

const extractActiveParamKeys = (
  preset: ITargetingPreset,
  categoriesCfg: Record<TargetingPresetCategory, ITargetingPresetCategory>,
  paramsCfg: Record<TargetingPresetParamKey, ITargetingPresetParam>,
  isSuperUser: boolean,
): Record<TargetingPresetCategory, TargetingPresetParamKey[]> => {
  const activeTargeting = {} as Record<TargetingPresetCategory, Set<TargetingPresetParamKey>>;
  Object.entries(categoriesCfg).forEach((item) => {
    const catKey = item[0] as TargetingPresetCategory;
    const paramKeys = item[1].paramKeys;
    activeTargeting[catKey] = new Set();
    // compare preset values to default values
    // if values are not the default, add the parameter to the active list
    paramKeys.forEach((paramKey) => {
      const param = paramsCfg[paramKey];
      // check user permissions
      if (param.isSuperUserOnly && !isSuperUser) return;
      if (!isEmpty(param.apiKeys)) {
        param.apiKeys.forEach((apiKey) => {
          if (preset[apiKey]) {
            // if the value is an array it must be checked differently
            if (Array.isArray(preset[apiKey])) {
              if (!isEqual(preset[apiKey], defaultTargetingPreset[apiKey])) {
                activeTargeting[catKey].add(param.key);
              }
            } else if (preset[apiKey] !== defaultTargetingPreset[apiKey]) {
              activeTargeting[catKey].add(param.key);
            }
          }
        });
      }
      if (!isEmpty(param.customUIConversions)) {
        const customAPIKey = param.customUIConversions.apiKey;
        if (preset[customAPIKey]) {
          // if the value is an array it must be checked differently
          if (Array.isArray(preset[customAPIKey])) {
            if (!isEqual(preset[customAPIKey], defaultTargetingPreset[customAPIKey])) {
              activeTargeting[catKey].add(paramKey);
            }
          } else if (preset[customAPIKey] !== defaultTargetingPreset[customAPIKey]) {
            activeTargeting[catKey].add(paramKey);
          }
        }
      }
    });
  });
  const result = {} as Record<TargetingPresetCategory, TargetingPresetParamKey[]>;
  Object.entries(activeTargeting).forEach((item) => {
    result[item[0] as TargetingPresetCategory] = Array.from(item[1]);
  });
  return result;
};

export const TargetingPresetFormView = () => {
  const { session, user, isSuperUser, location } = useBaseStore();
  const loc = parseLocation(location.pathname);
  const isNewPreset = !loc.targetingpresetid;
  const isClonedPreset = loc.presetid !== '';
  const isExistingPreset = !isNewPreset && !isClonedPreset;

  const { Tools } = useBaseConfig();
  const cfg = Tools.Targeting.Form as ITargetingPresetFormConfig;

  const [seed, setSeed] = useState(1);
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [listsLoadState, setListsLoadState] = useState(LoadingStateLoading);
  const [lists, setLists] = useState<Record<string, ISelectOption[]>>({});
  const [preset, setPreset] = useState(cloneDeep(defaultTargetingPreset as ITargetingPreset));
  const [name, setName] = useState('');
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [formErrors, setFormErrors] = useState({} as Record<TargetingPresetParamKey, string[]>);

  // conflicts drawer
  const [conflictLoadState, setConflictLoadState] = useState(LoadingStateNoData);
  const [lastRefreshed, setLastRefreshed] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [bidderConflicts, setBidderConflicts] = useState<IPresetConflict[]>([]);
  const [campaignConflicts, setCampaignConflicts] = useState<IPresetConflict[]>([]);

  // update dialog
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [updatePayload, setUpdatePayload] = useState<Partial<ITargetingPreset>>({});

  useEffect(() => {
    setName('');
    populateDropdowns();
    switch (true) {
      case isExistingPreset:
        getExistingPresetInfo(String(loc.targetingpresetid));
        break;
      case isClonedPreset:
        getExistingPresetInfo(String(loc.presetid || 0), true);
        break;
      case isNewPreset:
      default:
        const defaultPreset = applyPresetCustoms(
          cloneDeep(defaultTargetingPreset) as ITargetingPreset,
        );
        unstable_batchedUpdates(() => {
          setPreset(defaultPreset);
          setLoadState(LoadingStateHasData);
          setSeed(seed + 1);
        });
        break;
    }
  }, [location.pathname]);

  const populateDropdowns = () => {
    setListsLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];
    Object.values(cfg.dropdowns).forEach((dropdown) => {
      keys.push(dropdown.key);
      getListFns.push(dropdown.getList(session, user));
    });
    const tempLists: Record<string, ISelectOption[]> = {};
    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, i) => {
          tempLists[keys[i]] = result;
        });
        unstable_batchedUpdates(() => {
          setLists(tempLists);
          setListsLoadState(!isEmpty(tempLists) ? LoadingStateHasData : LoadingStateNoData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setLists({});
          setListsLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage('Error occurred while trying to load data.');
        });
      });
  };

  const applyPresetCustoms = (preset: ITargetingPreset) => {
    Object.values(cfg.params).forEach((param) => {
      if (!isEmpty(param.customUIConversions)) {
        const { apiKey, uiKey, convertResponse } = param.customUIConversions;
        // @ts-ignore // this is required because there cannot be a specific typing. this is handled in the config
        preset[uiKey] = convertResponse(preset[apiKey], session);
      }
    });
    return preset;
  };

  const getExistingPresetInfo = (presetID: string, isClone?: boolean) => {
    setLoadState(LoadingStateLoading);
    getTargetingPreset(session.publisher.ID, presetID)
      .then((res) => {
        if (res.Preset && !isEmpty(res.Preset)) {
          const completePreset = applyPresetCustoms(res.Preset);
          completePreset.ActiveParameters = extractActiveParamKeys(
            completePreset,
            cfg.categories,
            cfg.params,
            isSuperUser,
          );
          // if clone, clean associated bidders/campaigns
          if (isClone) {
            completePreset.Bidders = [];
            completePreset.Campaigns = [];
          }
          unstable_batchedUpdates(() => {
            setPreset(completePreset || ({} as ITargetingPreset));
            setName(isClonedPreset ? res.Preset?.Name + ' - Copy' : res.Preset?.Name || '');
            setLoadState(LoadingStateHasData);
          });
        } else {
          unstable_batchedUpdates(() => {
            setLoadState(LoadingStateNoData);
            setSnackbarMessage('Preset information not found.');
            setSnackbarError(true);
          });
        }
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setPreset({} as ITargetingPreset);
          setLoadState(LoadingStateNoData);
          setSnackbarMessage('Preset not found.');
          setSnackbarError(true);
        });
        navigate(RouteTargetingList, { p: session.publisher.ID });
      });
  };

  const generatePayload = () => {
    // prevent double save
    if (isSaveDisabled) {
      return;
    } else {
      unstable_batchedUpdates(() => {
        setIsSaveDisabled(true);
      });
    }

    // validate name
    const { sanitizedName, nameError } = validateName(name);
    sanitizedName;
    if (nameError) {
      unstable_batchedUpdates(() => {
        setSnackbarMessage(nameError);
        setSnackbarError(true);
        setIsSaveDisabled(false);
      });
      return;
    }

    // do not continue code if there are no selected parameters
    if (isEmpty(Object.values(preset.ActiveParameters).flat())) {
      unstable_batchedUpdates(() => {
        setSnackbarMessage('You must add at least one parameter to continue.');
        setSnackbarError(true);
        setIsSaveDisabled(false);
      });
      return;
    }

    const payload = {} as ITargetingPreset;
    const tempActiveParametersArray = Object.values(preset.ActiveParameters).flat() as string[];
    const errors = {} as Record<TargetingPresetParamKey, string[]>;

    // get active parameters
    (tempActiveParametersArray as TargetingPresetParamKey[]).forEach((paramKey) => {
      const parameterInfo = cfg.params[paramKey];

      if (!isEmpty(parameterInfo.customUIConversions)) {
        const { apiKey, uiKey, convertPayload } = parameterInfo.customUIConversions;
        const convertedResult = convertPayload(preset[uiKey as keyof ITargetingPreset]);
        // @ts-ignore
        payload[apiKey] = convertedResult.value;
        if (!isEmpty(convertedResult.errors)) {
          errors[paramKey] = convertedResult.errors;
        }
      }

      // custom validations
      if (!isEmpty(parameterInfo.customValidation)) {
        const { validateKey, validateFn } = parameterInfo.customValidation;
        const currentError = validateFn(preset[validateKey], parameterInfo.label);
        if (!isEmpty(currentError)) {
          errors[paramKey] = validateFn(preset[validateKey], parameterInfo.label);
        }
      }

      if (!isEmpty(parameterInfo.apiKeys)) {
        parameterInfo.apiKeys?.forEach((apiKey) => {
          // @ts-ignore
          payload[apiKey] = preset[apiKey];
        });
      }
    });

    if (!isEmpty(errors)) {
      unstable_batchedUpdates(() => {
        setFormErrors(errors);
        setSnackbarMessage('Please resolve all errors before continuing.');
        setSnackbarError(true);
        setIsSaveDisabled(false);
      });
      return;
    }

    // preserve superuser options when user is not a superuser
    if (!isSuperUser) {
      extractSuperUserOnlyTargetingPresetParamKeys().forEach((apiKey) => {
        // @ts-ignore
        payload[apiKey] = preset[apiKey];
      });
    }

    // update vars not associated with form builder
    payload.PublisherID = Number(session.publisher.ID);
    payload.Name = sanitizedName;
    // @ts-ignore
    payload.ActiveParameters = '';

    // revert missing parameters to their default values
    return { ...defaultTargetingPreset, ...payload };
  };

  const handleValidate = async (
    finalPayload: Record<string, any>,
    isValidateOnly?: boolean,
  ): Promise<boolean> => {
    if (!finalPayload || isEmpty(finalPayload)) return false;

    finalPayload.ID = preset.ID;
    setConflictLoadState(LoadingStateLoading);
    try {
      const { isValid, BidderConflicts, CampaignConflicts } = await validatePatchTargetingPreset(
        session.publisher.ID,
        finalPayload,
      );
      if (!isValid) {
        unstable_batchedUpdates(() => {
          setBidderConflicts(BidderConflicts);
          setCampaignConflicts(CampaignConflicts);
          setConflictLoadState(LoadingStateHasData);
          setIsSaveDisabled(false);
          setIsDrawerOpen(true);
          setLastRefreshed(moment().format('HH:mm:ss'));
        });
        return false;
      } else {
        unstable_batchedUpdates(() => {
          setBidderConflicts([]);
          setCampaignConflicts([]);
          setConflictLoadState(LoadingStateNoData);
          setLastRefreshed(moment().format('HH:mm:ss'));
          if (isValidateOnly) {
            setIsSaveDisabled(false);
          }
        });
        return true;
      }
    } catch (err) {
      console.error(err);
      unstable_batchedUpdates(() => {
        setSnackbarMessage('An error has occurred. Please check your values and try again.');
        setSnackbarError(true);
        setIsSaveDisabled(false);
        setConflictLoadState(LoadingStateNoData);
      });
      return false;
    }
  };

  const handleRefresh = () => {
    const finalPayload = generatePayload();
    if (finalPayload && !isEmpty(finalPayload)) {
      handleValidate(finalPayload, true);
    }
  };

  const handleOpenConflicts = () => {
    handleRefresh();
    setIsDrawerOpen(true);
  };

  const handleOnSave = async () => {
    const finalPayload = generatePayload();
    if (!finalPayload) return;

    if (isNewPreset || isClonedPreset) {
      createTargetingPreset(session.publisher.ID, finalPayload)
        .then((res) => {
          setIsSaveDisabled(false);
          navigate(getTargetingPresetEditRoute(res.ID, session, user));
          setSnackbarMessage(`Preset ${finalPayload.Name} has been successfully created.`);
          setSnackbarError(false);
          setFormErrors({} as Record<TargetingPresetParamKey, string[]>);
        })
        .catch((err) => {
          console.error(err);
          unstable_batchedUpdates(() => {
            setSnackbarMessage('An error has occurred. Please check your values and try again.');
            setSnackbarError(true);
            setIsSaveDisabled(false);
          });
        });
    } else {
      finalPayload.ID = preset.ID;
      const isValid = await handleValidate(finalPayload);
      if (!isValid) return;

      // if preset has associated bidders, send info to update dialog for final confirmation and action
      if (!isEmpty(preset.TargetingBidderIDs) || !isEmpty(preset.TargetingCampaignIDs)) {
        unstable_batchedUpdates(() => {
          setUpdatePayload(finalPayload);
          setOpenUpdateDialog(true);
        });
      } else {
        patchTargetingPreset(session.publisher.ID, finalPayload)
          .then((res) => {
            if (res.Preset) {
              const completePreset = applyPresetCustoms(res.Preset);
              completePreset.ActiveParameters = extractActiveParamKeys(
                completePreset,
                cfg.categories,
                cfg.params,
                isSuperUser,
              );
              unstable_batchedUpdates(() => {
                setSeed(seed + 1);
                setPreset(completePreset || ({} as ITargetingPreset));
                setName(res.Preset?.Name || '');
                setSnackbarMessage(
                  `Preset ${res.Preset?.Name || ''} has been successfully updated.`,
                );
                setSnackbarError(false);
                setFormErrors({} as Record<TargetingPresetParamKey, string[]>);
                setIsSaveDisabled(false);
              });
            } else {
              unstable_batchedUpdates(() => {
                setSnackbarMessage('An error has occurred.');
                setSnackbarError(true);
                setIsSaveDisabled(false);
              });
            }
          })
          .catch((err) => {
            console.error(err);
            unstable_batchedUpdates(() => {
              setSnackbarMessage('An error has occurred. Please check your values and try again.');
              setSnackbarError(true);
              setIsSaveDisabled(false);
            });
          });
      }
    }
  };

  const handleUpdateSuccess = (updatedPreset: ITargetingPreset) => {
    const completePreset = applyPresetCustoms(updatedPreset);
    completePreset.ActiveParameters = extractActiveParamKeys(
      completePreset,
      cfg.categories,
      cfg.params,
      isSuperUser,
    );
    unstable_batchedUpdates(() => {
      setSeed(seed + 1);
      setPreset(completePreset || ({} as ITargetingPreset));
      setName(updatedPreset.Name || '');
      setSnackbarMessage(`Preset ${updatedPreset.Name || ''} has been successfully updated.`);
      setSnackbarError(false);
      setFormErrors({} as Record<TargetingPresetParamKey, string[]>);
      setIsSaveDisabled(false);
      setOpenUpdateDialog(false);
    });
  };

  const handleCloseUpdateDialog = () => {
    unstable_batchedUpdates(() => {
      setOpenUpdateDialog(false);
      setIsSaveDisabled(false);
    });
  };

  const isPresetActive =
    isExistingPreset && (!isEmpty(preset.Bidders) || !isEmpty(preset.Campaigns));

  const numConflicts = (bidderConflicts || []).length + (campaignConflicts || []).length;

  const isLoaded = loadState !== LoadingStateLoading && listsLoadState !== LoadingStateLoading;
  if (loadState === LoadingStateLoading) return null;

  return (
    <TargetingPresetFormContext.Provider value={{ lists }}>
      <Panels max vertical relative minWidthMain>
        <Panel largeHorizontalPadding smallVerticalPadding>
          <TopBar title='Targeting Preset' enableUINotices />
        </Panel>
        <Panel
          largeHorizontalPadding
          smallVerticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          {/* TITLE - SAVE CLOSE */}
          <Cols
            gap={spacing.base}
            style={{ justifyContent: 'space-between', alignItems: 'center', minHeight: '40px' }}
          >
            <Col flex>
              <Cols gap={spacing.huge} style={{ alignItems: 'center' }}>
                <Col>
                  <TextEditor
                    large
                    name={name || preset.Name || 'New Preset'}
                    placeholder='Enter Preset Name'
                    handleCloseEditor={(text) => {
                      unstable_batchedUpdates(() => {
                        setName(text);
                        setIsSaveDisabled(false);
                      });
                    }}
                    disabled={isNewPreset}
                  />
                </Col>
                {/* ASSOCIATED BADGES */}
                <Col>
                  <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                    {cfg.badges.map((badge) => (
                      <Col>
                        <TargetingPresetAssociatedBadge
                          items={preset[badge.dataKey] as IItemIDNameRoute[]}
                          singularLabel={badge.singularLabel}
                          pluralLabel={badge.pluralLabel}
                          color={badge.color}
                          iconName={badge.iconName}
                          modalTitle={badge.modalTitle}
                          modalSubTitle={preset.Name}
                        />
                      </Col>
                    ))}
                  </Cols>
                </Col>
              </Cols>
            </Col>
            <Col>
              <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                <Col>
                  <ButtonV2 small onClick={handleOnSave} disabled={!isLoaded || isSaveDisabled}>
                    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                      <Col displayFlex paddingBottom='1px'>
                        <IconV2
                          name={isPresetActive ? 'sync' : 'save'}
                          color={!isLoaded ? publicaColors.gray : publicaColors.white}
                          small
                        />
                      </Col>
                      <Col>{isPresetActive ? 'Review & Save' : 'Save'}</Col>
                    </Cols>
                  </ButtonV2>
                </Col>
                <Col>
                  <ButtonV2
                    small
                    style={{ paddingLeft: '13px' }}
                    clearBackground
                    onClick={() => navigate(RouteTargetingList, { p: session.publisher.ID })}
                  >
                    <Cols gap='2px' style={{ alignItems: 'center' }}>
                      <Col displayFlex paddingBottom='1px'>
                        <IconV2 name='close' color={publicaColors.gray} large />
                      </Col>
                      <Col>Close</Col>
                    </Cols>
                  </ButtonV2>
                </Col>
              </Cols>
            </Col>
          </Cols>
        </Panel>
        <Panel scroll largeHorizontalPadding paddingTop={spacing.large} paddingBottom='48px'>
          <Rows gap={spacing.small}>
            {/* NOTES */}
            <Row noPadding>
              <Text
                small
                style={{
                  backgroundColor: publicaColors.skyBlue,
                  padding: `${spacing.small} ${spacing.base}`,
                  minWidth: '800px',
                }}
              >
                Use Presets as a subset of targeting parameters that can be shared between bidders
                or campaigns, much like a baseline that you can update from one place. We recommend
                creating Presets for each categories (Supply, Ad Break, Users, Customs) presented
                below to avoid creating conflicts as you combine them. Head over to bidder targeting
                to apply Presets and follow validation guidance.
              </Text>
            </Row>
            {/* CONFLICT WARNING */}
            {numConflicts > 0 && (
              <Row noPadding>
                <Cols
                  gap={spacing.large}
                  style={{
                    alignItems: 'center',
                    minWidth: '800px',
                    backgroundColor: publicaColors.pink,
                    padding: `${spacing.small} ${spacing.base}`,
                  }}
                >
                  <Col displayFlex>
                    <IconV2 name='umbrella' small />
                  </Col>
                  <Col>
                    <Text small bold>
                      {`We've found ${numConflicts} conflicting item${
                        numConflicts > 1 ? 's' : ''
                      } that ${
                        numConflicts > 1 ? 'are' : 'is'
                      } using this preset. Please resolve them before safely saving edits.`}
                      <Span
                        bold
                        color={publicaColors.lightBlue}
                        style={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          backgroundColor: publicaColors.veryLightGray,
                          borderRadius: borderRadius.small,
                          padding: '2px 8px',
                          margin: '0 8px',
                        }}
                        onClick={handleOpenConflicts}
                      >
                        » View Conflicts
                      </Span>
                    </Text>
                  </Col>
                </Cols>
              </Row>
            )}
            <Row noPadding>
              <Rows style={{ ...highlightedDivStyle, minWidth: '940px' }}>
                {/* CREATE NAME */}
                {isNewPreset && (
                  <Row style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
                    <Cols style={{ alignItems: 'center' }}>
                      {/* LABEL */}
                      <Col width='160px'>
                        <Text small bold>
                          Preset Name
                        </Text>
                      </Col>
                      <Col>
                        <TextInput
                          placeholder='New Preset'
                          minLength={3}
                          maxLength={99}
                          isAutoFocused={isNewPreset}
                          handleOnChange={(text) => setName(text)}
                          inputSize='small'
                          width='400px'
                          initialValue={name}
                          key={seed}
                          inputID='presetname'
                        />
                      </Col>
                    </Cols>
                  </Row>
                )}
                {/* ALL FORM BUILDERS */}
                {Object.values(cfg.categories).map((cat, i) => {
                  return (
                    <Row
                      smallVerticalPadding
                      style={{
                        borderTop: i === 0 ? 'none' : `1px solid ${publicaColors.lightGray}`,
                      }}
                    >
                      <Rows>
                        {/* CATEGORY AND ICON */}
                        <Row noPadding>
                          <Cols style={{ alignItems: 'center' }}>
                            <Col displayFlex>
                              <div style={{ width: iconColWidth, height: '100%', display: 'flex' }}>
                                <IconV2 name={cat.iconName} small />
                              </div>
                            </Col>
                            <Col>
                              <Text bold>{cat.key}</Text>
                            </Col>
                          </Cols>
                        </Row>
                        <Row noPadding>
                          <Rows paddingLeft={iconColWidth}>
                            {/* DESCRIPTION NOTES */}
                            <Row noPadding>
                              <Text small muted>
                                {cat.subText}
                              </Text>
                            </Row>
                            <Row noPadding>
                              <Text small bold>
                                Where
                              </Text>
                            </Row>
                            {/* FORM BUILDER */}
                            <Row noPadding>
                              <FormBuilder
                                object={preset}
                                defaultObject={defaultTargetingPreset}
                                parameterOptions={generateTargetingPresetParameterOptions(
                                  cat.paramKeys,
                                  session.publisher.ID,
                                  isSuperUser,
                                )}
                                parameterCfg={cfg.params}
                                lists={lists}
                                initialParameterKeys={
                                  (
                                    preset.ActiveParameters as Record<
                                      TargetingPresetCategory,
                                      string[]
                                    >
                                  )[cat.key] || []
                                }
                                isLoading={!isLoaded}
                                key={seed}
                                customAddText={cat.addParamText}
                                errors={formErrors}
                                activeParametersKey={cat.key}
                              />
                            </Row>
                          </Rows>
                        </Row>
                      </Rows>
                    </Row>
                  );
                })}
              </Rows>
            </Row>
          </Rows>
        </Panel>
        <TargetingConflictsDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          bidderConflicts={bidderConflicts}
          campaignConflicts={campaignConflicts}
          timestamp={lastRefreshed}
          loadState={conflictLoadState}
          handleRefresh={handleRefresh}
        />
        <UpdateTargetingPresetDialog
          open={openUpdateDialog}
          handleClose={handleCloseUpdateDialog}
          preset={updatePayload}
          handleSuccess={handleUpdateSuccess}
          publisherID={session.publisher.ID}
          setSnackbarError={setSnackbarError}
          setSnackbarMessage={setSnackbarMessage}
          numBidders={preset?.Bidders?.length || 0}
          numCampaigns={preset?.Campaigns?.length || 0}
        />
        <SnackbarView
          open={!!snackbarMessage}
          error={!!snackbarError}
          onClose={() => setSnackbarMessage('')}
        >
          {snackbarMessage}
        </SnackbarView>
      </Panels>
    </TargetingPresetFormContext.Provider>
  );
};
